import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { PersonWithDetails, UpdatePersonRequest } from '../../types/person.js';
import * as personApi from '../../services/person-api.js';
import { navigateTo } from '../../services/router.js';
import './cp-person-section-editor.js';
import './cp-person-tag-input.js';
import './cp-person-links.js';
import '../convo/cp-convo-partner-profile.js';

const CATEGORIES = ['Familie', 'Geschaeftlich', 'Kunde', 'Freund', 'Bekannter'];

const RELATIONSHIP_TYPES = [
  { value: 'personal', label: 'Persoenlich' },
  { value: 'business', label: 'Geschaeftlich' },
  { value: 'family', label: 'Familie' },
  { value: 'friend', label: 'Freund' },
  { value: 'acquaintance', label: 'Bekannter' },
];

const FORM_OF_ADDRESS_OPTIONS: Array<{ value: 'du' | 'sie' | 'unbekannt'; label: string }> = [
  { value: 'unbekannt', label: 'Unbekannt' },
  { value: 'du', label: 'Du' },
  { value: 'sie', label: 'Sie' },
];

@customElement('cp-person-detail')
export class CpPersonDetail extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) person: PersonWithDetails | null = null;

  @state() private _editing = false;
  @state() private _saving = false;
  @state() private _deleting = false;
  @state() private _confirmDelete = false;
  @state() private _error: string | null = null;
  @state() private _editData: UpdatePersonRequest = {};

  private _getInitials(name: string): string {
    return name
      .split(' ')
      .slice(0, 2)
      .map(n => n.charAt(0).toUpperCase())
      .join('');
  }

  private _startEdit(): void {
    if (!this.person) return;
    this._editData = {
      full_name: this.person.full_name,
      nickname: this.person.nickname ?? '',
      category: this.person.category,
      relationship_type: this.person.relationship_type,
      email: this.person.email ?? '',
      phone: this.person.phone ?? '',
      birthday: this.person.birthday ?? '',
      subtitle: this.person.subtitle ?? '',
      form_of_address: this.person.form_of_address,
    };
    this._editing = true;
  }

  private _cancelEdit(): void {
    this._editing = false;
    this._error = null;
  }

  private _handleFieldInput(field: keyof UpdatePersonRequest, e: Event): void {
    this._editData = { ...this._editData, [field]: (e.target as HTMLInputElement).value };
  }

  private async _saveEdit(): Promise<void> {
    if (!this.person) return;
    this._saving = true;
    this._error = null;
    try {
      await personApi.updatePerson(this.person.id, this._editData);
      this._editing = false;
      this._dispatchUpdated();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private _handleDelete(): void {
    this._confirmDelete = true;
  }

  private _cancelDelete(): void {
    this._confirmDelete = false;
  }

  private async _confirmDeletePerson(): Promise<void> {
    if (!this.person) return;
    this._deleting = true;
    try {
      await personApi.deletePerson(this.person.id);
      this.dispatchEvent(new CustomEvent('person-deleted', { bubbles: true, composed: true }));
      navigateTo('/people');
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Loeschen';
      this._confirmDelete = false;
    } finally {
      this._deleting = false;
    }
  }

  private _dispatchUpdated(): void {
    this.dispatchEvent(new CustomEvent('person-updated', { bubbles: true, composed: true }));
  }

  render() {
    if (!this.person) {
      return html`<div class="cp-person-detail__loading">Lade Person...</div>`;
    }
    const p = this.person;
    return html`
      <div class="cp-person-detail">
        ${this._renderHeader(p)}
        ${this._editing ? this._renderEditForm() : this._renderInfo(p)}
        <div class="cp-person-detail__sections">
          <cp-person-section-editor
            .personId=${p.id}
            .sections=${p.sections}
            @sections-updated=${this._dispatchUpdated}
          ></cp-person-section-editor>
        </div>
        <div class="cp-person-detail__tags">
          <cp-person-tag-input
            .personId=${p.id}
            .tags=${p.tags}
            @tags-updated=${this._dispatchUpdated}
          ></cp-person-tag-input>
        </div>
        ${p.links.length > 0 ? html`
          <div class="cp-person-detail__links">
            <cp-person-links .links=${p.links}></cp-person-links>
          </div>
        ` : nothing}
        <div class="cp-person-detail__partner-profile">
          <cp-convo-partner-profile .personId=${p.id}></cp-convo-partner-profile>
        </div>
      </div>
    `;
  }

  private _renderHeader(p: PersonWithDetails) {
    return html`
      <div class="cp-person-detail__header">
        <button class="cp-person-detail__back-btn" @click=${() => navigateTo('/people')}>
          ← Zurück
        </button>
        <div class="cp-person-detail__header-content">
          <div class="cp-person-detail__avatar">${this._getInitials(p.full_name)}</div>
          <div class="cp-person-detail__header-info">
            <h1 class="cp-person-detail__name">${p.full_name}</h1>
            ${p.subtitle ? html`<p class="cp-person-detail__subtitle">${p.subtitle}</p>` : nothing}
            <span class="cp-person-detail__category">${p.category}</span>
          </div>
          ${!this._editing ? html`
            <div class="cp-person-detail__header-actions">
              <button class="cp-person-detail__edit-btn" @click=${this._startEdit}>Bearbeiten</button>
              <button class="cp-person-detail__delete-btn" @click=${this._handleDelete}>Loeschen</button>
            </div>
          ` : nothing}
        </div>
        ${this._confirmDelete ? html`
          <div class="cp-person-detail__confirm-bar">
            <span>Person wirklich loeschen?</span>
            <div class="cp-person-detail__confirm-bar-actions">
              <button
                class="cp-person-detail__cancel-btn"
                @click=${this._cancelDelete}
                ?disabled=${this._deleting}
              >Abbrechen</button>
              <button
                class="cp-person-detail__delete-btn"
                @click=${() => void this._confirmDeletePerson()}
                ?disabled=${this._deleting}
              >${this._deleting ? 'Loeschen...' : 'Loeschen'}</button>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderInfo(p: PersonWithDetails) {
    const hasAnyField = p.nickname || p.relationship_type || p.birthday || p.email || p.phone || p.form_of_address !== 'unbekannt';
    if (!hasAnyField) return nothing;
    return html`
      <div class="cp-person-detail__info">
        ${p.nickname ? html`
          <div class="cp-person-detail__field">
            <span class="cp-person-detail__label">Rufname</span>
            <span class="cp-person-detail__value">${p.nickname}</span>
          </div>
        ` : nothing}
        ${p.relationship_type ? html`
          <div class="cp-person-detail__field">
            <span class="cp-person-detail__label">Beziehung</span>
            <span class="cp-person-detail__value">${p.relationship_type}</span>
          </div>
        ` : nothing}
        ${p.form_of_address !== 'unbekannt' ? html`
          <div class="cp-person-detail__field">
            <span class="cp-person-detail__label">Anrede</span>
            <span class="cp-person-detail__value">${p.form_of_address === 'du' ? 'Du' : 'Sie'}</span>
          </div>
        ` : nothing}
        ${p.birthday ? html`
          <div class="cp-person-detail__field">
            <span class="cp-person-detail__label">Geburtstag</span>
            <span class="cp-person-detail__value">${p.birthday}</span>
          </div>
        ` : nothing}
        ${p.email ? html`
          <div class="cp-person-detail__field">
            <span class="cp-person-detail__label">E-Mail</span>
            <a class="cp-person-detail__value cp-person-detail__link" href="mailto:${p.email}">${p.email}</a>
          </div>
        ` : nothing}
        ${p.phone ? html`
          <div class="cp-person-detail__field">
            <span class="cp-person-detail__label">Telefon</span>
            <a class="cp-person-detail__value cp-person-detail__link" href="tel:${p.phone}">${p.phone}</a>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderEditForm() {
    const d = this._editData;
    return html`
      <form class="cp-person-detail__form" @submit=${(e: Event) => { e.preventDefault(); void this._saveEdit(); }}>
        ${this._error ? html`<p class="cp-person-detail__form-error">${this._error}</p>` : nothing}
        <div class="cp-person-detail__form-grid">
          <label class="cp-person-detail__form-label">
            Name *
            <input
              class="cp-person-detail__form-input"
              type="text"
              required
              .value=${d.full_name ?? ''}
              @input=${(e: Event) => this._handleFieldInput('full_name', e)}
            />
          </label>
          <label class="cp-person-detail__form-label">
            Rufname
            <input
              class="cp-person-detail__form-input"
              type="text"
              .value=${d.nickname ?? ''}
              @input=${(e: Event) => this._handleFieldInput('nickname', e)}
            />
          </label>
          <label class="cp-person-detail__form-label">
            Kategorie
            <select
              class="cp-person-detail__form-input"
              .value=${d.category ?? ''}
              @change=${(e: Event) => this._handleFieldInput('category', e)}
            >
              <option value="">Keine Kategorie</option>
              ${CATEGORIES.map(c => html`<option value=${c} ?selected=${d.category === c}>${c}</option>`)}
            </select>
          </label>
          <label class="cp-person-detail__form-label">
            Beziehungstyp
            <select
              class="cp-person-detail__form-input"
              .value=${d.relationship_type ?? ''}
              @change=${(e: Event) => this._handleFieldInput('relationship_type', e)}
            >
              <option value="">Kein Typ</option>
              ${RELATIONSHIP_TYPES.map(r => html`<option value=${r.value} ?selected=${d.relationship_type === r.value}>${r.label}</option>`)}
            </select>
          </label>
          <label class="cp-person-detail__form-label">
            Anrede
            <select
              class="cp-person-detail__form-input"
              .value=${d.form_of_address ?? 'unbekannt'}
              @change=${(e: Event) => this._handleFieldInput('form_of_address', e)}
            >
              ${FORM_OF_ADDRESS_OPTIONS.map(o => html`<option value=${o.value} ?selected=${d.form_of_address === o.value}>${o.label}</option>`)}
            </select>
          </label>
          <label class="cp-person-detail__form-label">
            E-Mail
            <input
              class="cp-person-detail__form-input"
              type="email"
              .value=${d.email ?? ''}
              @input=${(e: Event) => this._handleFieldInput('email', e)}
            />
          </label>
          <label class="cp-person-detail__form-label">
            Telefon
            <input
              class="cp-person-detail__form-input"
              type="tel"
              .value=${d.phone ?? ''}
              @input=${(e: Event) => this._handleFieldInput('phone', e)}
            />
          </label>
          <label class="cp-person-detail__form-label">
            Geburtstag
            <input
              class="cp-person-detail__form-input"
              type="date"
              .value=${d.birthday ?? ''}
              @input=${(e: Event) => this._handleFieldInput('birthday', e)}
            />
          </label>
          <label class="cp-person-detail__form-label cp-person-detail__form-label--full">
            Subtitle
            <input
              class="cp-person-detail__form-input"
              type="text"
              .value=${d.subtitle ?? ''}
              @input=${(e: Event) => this._handleFieldInput('subtitle', e)}
            />
          </label>
        </div>
        <div class="cp-person-detail__form-actions">
          <button
            type="button"
            class="cp-person-detail__cancel-btn"
            @click=${this._cancelEdit}
            ?disabled=${this._saving}
          >Abbrechen</button>
          <button
            type="submit"
            class="cp-person-detail__save-btn"
            ?disabled=${this._saving}
          >${this._saving ? 'Speichern...' : 'Speichern'}</button>
        </div>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-person-detail': CpPersonDetail;
  }
}
